import { useMarketplacePreviewItems } from '@/apis/marketplace-preview/hooks/useMarketplacePreviewItems'
import { useMarketplacePreviewStores } from '@/apis/marketplace-preview/hooks/useMarketplacePreviewStores'
import { useApplicantProgramYears } from '@/app/SchoolYearProvider'
import { usePagination } from '@/common/hooks/usePagination.tsx'
import { useDebounce } from '@uidotdev/usehooks'
import { useState } from 'react'
import { useMarketplacePreviewAffiliations } from '@/apis/marketplace-preview/hooks/useMarketplacePreviewAffiliations'
import { MarketplaceAccessMode } from '@/apis/marketplace-preview/types'

type MarketplacePreviewView = 'stores' | 'items' | 'affiliations'

type MarketplacePreviewDataOptions = {
  // Scopes the items query to a single store, used by the provider details view.
  providerId?: number
}

export function useMarketplacePreviewData(
  mode: MarketplaceAccessMode = 'preview',
  { providerId }: MarketplacePreviewDataOptions = {}
) {
  // Local state
  const [itemSearch, setItemSearch] = useState('')
  const [storeSearch, setStoreSearch] = useState('')
  const [affiliationSearch, setAffiliationSearch] = useState('')
  const [currentView, setCurrentView] =
    useState<MarketplacePreviewView>('stores')
  const [selectedFilter, setSelectedFilter] = useState<string>('')
  const [selectedAvailability, setSelectedAvailability] = useState<
    number | undefined
  >(undefined)
  const debouncedStoreSearch = useDebounce(storeSearch, 500)

  // Derived state
  const debouncedItemSearch = useDebounce(itemSearch, 500)
  const debouncedAffiliationSearch = useDebounce(affiliationSearch, 500)
  const { selectedProgramId } = useApplicantProgramYears()
  const { currentPage, perPage, setCurrentPage, setPerPage } = usePagination()

  // Data fetching
  const storesQuery = useMarketplacePreviewStores(
    {
      perPage,
      // Kept on the first page while another view drives the pagination, so
      // paging through items or affiliations does not refetch the stores.
      page: currentView === 'stores' ? currentPage : 1,
      search: debouncedStoreSearch,
      business_type: selectedFilter,
    },
    mode
  )
  const itemsQuery = useMarketplacePreviewItems(
    {
      perPage,
      page: currentPage,
      search: debouncedItemSearch,
      business_type: selectedFilter,
      provider_id: providerId,
    },
    mode
  )
  const affiliationsQuery = useMarketplacePreviewAffiliations(
    {
      perPage,
      page: currentPage,
      search: debouncedAffiliationSearch,
    },
    mode,
    {
      enabled: currentView === 'affiliations',
    }
  )

  const handleFilterOnChange = (item: { id: number; name: string }) => {
    setSelectedFilter(
      item.id == 0 ? '' : item.id == 1 ? '1,2' : item.id.toString()
    )
    setCurrentPage(1)
  }

  const handleAvailabilityFilterOnChange = (item: {
    id: number
    name: string
  }) => {
    setSelectedAvailability(item.id === 0 ? undefined : item.id === 1 ? 1 : 0)
    setCurrentPage(1)
  }

  const handleToggleView = (view: MarketplacePreviewView) => {
    setCurrentView(view)
    setItemSearch('')
    setStoreSearch('')
    setAffiliationSearch('')
    setSelectedFilter('')
    setCurrentPage(1)
  }

  return {
    // queries
    storesQuery,
    itemsQuery,
    affiliationsQuery,

    // Search
    itemSearch,
    setItemSearch,
    storeSearch,
    setStoreSearch,
    affiliationSearch,
    setAffiliationSearch,

    //State
    selectedFilter,
    selectedProgramId,
    selectedAvailability,
    currentView,

    // Pagination
    currentPage,
    perPage,
    setCurrentPage,
    setPerPage,

    // Handlers
    handleFilterOnChange,
    handleToggleView,
    handleAvailabilityFilterOnChange,
  }
}
